import React from 'react'
import { ActivityIndicator } from 'react-native'

import { zodResolver } from '@hookform/resolvers/zod'
import { publishEvent } from '@zet/analytics'
import { useToast } from '@zet/common/components/toast'
import { AgentDetails } from '@zet/customer'
import { Button, Stack, colors } from '@zet/design-system'
import { i18n } from '@zet/localization'
import moment from 'moment'
import 'react-datepicker/dist/react-datepicker.css'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import {
  useGetCriteriaIndexes,
  useGetNextStateForProductFlow,
} from '../../../../api/brands/hooks'
import { BottomsheetTypes } from '../../../../api/brands/types'
import { useProductFlowApply } from '../../../../hooks/useProductFlowApply'
import { Header } from '../header'
import { FormFields } from './formFields'

const validationSchema = z.object({
  panNumber: z
    .string()
    .min(1, { message: 'Field is required!' })
    .regex(
      new RegExp(/(^([a-zA-Z]{5})([0-9]{4})([a-zA-Z]{1})$)/),
      'Invalid Pan Number',
    )
    .max(10, 'Invalid Pan Number'),
  dob: z.date(),
  salaryType: z.string({ required_error: 'Please select a type' }),
  employmentType: z.string({ required_error: 'Please select a type' }),
  income: z
    .string()
    .min(1, { message: 'Income is required' })
    .max(99999999, { message: 'Maximum limit exceeded' })
    .regex(/^([1-9]\d*)(\.\d+)?$/, {
      message: 'Please enter valid income',
    }),
})
type ValidationSchema = z.infer<typeof validationSchema>
const EmploymentDetails = ({
  agentData,
  handleBottomsheetState,
}: {
  agentData: AgentDetails
  handleBottomsheetState: (state: BottomsheetTypes, props?: any) => void
}) => {
  const toast = useToast()
  const {
    control,
    formState: { errors, isValid },
    setValue,
    setError,
    handleSubmit,
    getValues,
  } = useForm<ValidationSchema>({
    mode: 'onChange',
    resolver: zodResolver(validationSchema),
  })
  const { data: criteriaData, refetch } = useGetCriteriaIndexes({
    locale: i18n.locale,
  })
  const { mutate, isLoading } = useGetNextStateForProductFlow()

  const { apply, shareLinkLoading } = useProductFlowApply(
    agentData,
    handleBottomsheetState,
  )

  function closeBottomSheet() {
    handleBottomsheetState(null)
  }

  function onSubmit(values: {
    employmentType: string
    dob: string
    panNumber: any
    salaryType: string
    income: any
  }) {
    publishEvent('click_action', {
      item_name: 'shortflow_employment_details',
    })
    const employmentData = criteriaData?.find(
      (value) => value.criteriaName === 'employmentType',
    )
    const employmentD = employmentData?.fields?.find(
      (value) => value?.displayValue === values?.employmentType,
    )
    const dob = moment(values?.dob).format('YYYY-MM-DD')
    mutate(
      {
        customerPayload: {
          panNo: values?.panNumber,
          modeOfIncome: values?.salaryType === 'Cash' ? 2 : 1,
          employmentType: employmentD?.key,
          annualIncome: values?.income,
          dob: dob,
          customerId: sessionStorage.getItem('customerId'),
        },
        userState: 'SHOWN_BASIC_DETAILS_SCREEN_V3',
      },
      {
        onSuccess(data) {
          if (data?.code === 400) {
            setError('panNumber', {
              message: data?.message,
            })
          }
          if (data?.action === 'SHOW_SECONDARY_PHONE_NUMBER_SCREEN_V3') {
            handleBottomsheetState('SECONDARY_PHONE_NUMBERS', data)
          }
          if (data?.action === 'SHOW_RECOMMENDATION_SCREEN') {
            apply()
          }
        },
        onError(err) {
          toast?.show({
            intent: 'error',
            heading: i18n.t('toastMessages.error'),
            description:
              err?.response?.data?.error?.message ||
              i18n.t('toastMessages.somethingWentWrong'),
            hideAfter: 3000,
          })
        },
      },
    )
  }

  return (
    <>
      <Stack className="pb-10">
        <Header
          title={i18n.t('addCusProductjourney.enterEmploymentDetails')}
          closeBottomSheet={closeBottomSheet}
        />
        {isLoading || shareLinkLoading ? (
          <Stack className="pt-6">
            <ActivityIndicator
              size={'large'}
              color={colors.primary[100]}
              className="items-center justify-center"
            />
          </Stack>
        ) : (
          <FormFields
            control={control}
            errors={errors}
            setValue={setValue}
            criteriaData={criteriaData}
            refetch={refetch}
            getValues={getValues}
          />
        )}
      </Stack>
      <Stack className="bg-neutral-white100 absolute bottom-0 w-full px-3 py-2">
        <Button
          className="bg-tertiary-200 w-full"
          disabled={!isValid}
          size={'lg'}
          onPress={handleSubmit(onSubmit)}
          loading={isLoading || shareLinkLoading}
        >
          {i18n.t('bottomView.continue')}
        </Button>
      </Stack>
    </>
  )
}

export { EmploymentDetails }
